import {
  CustomCollectionEntryWithFields,
  customCollectionsApi,
  CustomCollectionWithCount,
} from '@/lib/api/custom-collections.api'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery } from '@tanstack/react-query'

import type {
  CustomCollection,
  CustomCollectionEntry,
  CustomCollectionFeatureConfig,
  CustomCollectionRelationDefinition,
  DropdownOption,
  PaginatedResponse,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'

export interface UseCustomCollectionsQueryParams {
  page?: number
  limit?: number
  search?: string
  columnFilters?: Record<string, unknown>
}

/**
 * Hook to fetch all custom collections (backward compatible - returns array)
 */
export function useCustomCollectionsQuery(options?: {
  enabled?: boolean
  params?: UseCustomCollectionsQueryParams
}) {
  const { currentOrganization } = useOrganizationStore()
  const params = options?.params

  return useQuery<
    CustomCollectionWithCount[] | PaginatedResponse<CustomCollectionWithCount>,
    Error,
    CustomCollectionWithCount[]
  >({
    queryKey: queryKeys.customCollections.list(
      params as Record<string, unknown> | undefined,
    ),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required')
      }
      const response = await customCollectionsApi.getCollections(params ?? {})
      return response.data
    },
    select: data => {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.data)) return data.data
      return []
    },
    enabled: (options?.enabled !== false && !!currentOrganization?.id) ?? false,
    placeholderData: previous => previous,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch all custom collections with pagination and filtering (paginated version)
 */
export function useCustomCollectionsPaginatedQuery(
  params: UseCustomCollectionsQueryParams = {},
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<PaginatedResponse<CustomCollectionWithCount>>({
    queryKey: queryKeys.customCollections.list(
      params as Record<string, unknown>,
    ),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required')
      }
      return customCollectionsApi.getCollections(params)
    },
    enabled: (options?.enabled !== false && !!currentOrganization?.id) ?? false,
    placeholderData: previous => previous,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch a single custom collection by ID
 */
export function useCustomCollectionQuery(
  id: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<CustomCollection>({
    queryKey: queryKeys.customCollections.detail(id!),
    queryFn: async () => {
      if (!id) {
        throw new Error('Collection ID is required')
      }
      return customCollectionsApi.getCollection(id)
    },
    enabled:
      (options?.enabled !== false && !!id && !!currentOrganization?.id) ??
      false,
  })
}

/**
 * Hook to fetch a single custom collection by slug
 */
export function useCustomCollectionBySlugQuery(
  slug: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<CustomCollection>({
    queryKey: queryKeys.customCollections.detail(slug || ''),
    queryFn: async () => {
      if (!slug) {
        throw new Error('Collection slug is required')
      }
      return customCollectionsApi.getCollectionBySlug(slug)
    },
    enabled:
      (options?.enabled !== false && !!slug && !!currentOrganization?.id) ??
      false,
  })
}

export interface UseCustomCollectionEntriesQueryParams {
  page?: number
  limit?: number
  search?: string
  columnFilters?: Record<string, unknown>
}

/**
 * Hook to fetch entries for a custom collection (backward compatible - returns array)
 */
export function useCustomCollectionEntriesQuery(
  collectionId: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<CustomCollectionEntryWithFields[]>({
    queryKey: queryKeys.customCollections.entries(collectionId!),
    queryFn: async () => {
      if (!collectionId) {
        throw new Error('Collection ID is required')
      }
      const response = await customCollectionsApi.getEntries(collectionId, {})
      return response.data
    },
    enabled:
      (options?.enabled !== false &&
        !!collectionId &&
        !!currentOrganization?.id) ??
      false,
  })
}

/**
 * Hook to fetch a single custom collection entry by ID
 */
export function useCustomCollectionEntryQuery(
  collectionId: string | undefined,
  entryId: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<CustomCollectionEntryWithFields>({
    queryKey: queryKeys.customCollections.entry(collectionId!, entryId!),
    queryFn: async () => {
      if (!collectionId || !entryId) {
        throw new Error('Collection ID and entry ID are required')
      }
      return customCollectionsApi.getEntry(collectionId, entryId)
    },
    enabled:
      (options?.enabled !== false &&
        !!collectionId &&
        !!entryId &&
        !!currentOrganization?.id) ??
      false,
  })
}

/**
 * Hook to resolve an entry id to its collection (id + slug), for links that
 * only know the entry — entry pages live at /collections/:slug/:entryId.
 */
export function useCustomCollectionEntryLocationQuery(
  entryId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery({
    queryKey: queryKeys.customCollections.entryLocation(entryId ?? ''),
    queryFn: () => customCollectionsApi.getEntryLocation(entryId!),
    enabled:
      options?.enabled !== false && !!entryId && !!currentOrganization?.id,
    // An entry never moves between collections; a renamed slug is redirected.
    staleTime: Infinity,
  })
}

/**
 * Hook to fetch entries for a custom collection with pagination and filtering (paginated version)
 */
export function useCustomCollectionEntriesPaginatedQuery(
  collectionId: string | undefined,
  params: UseCustomCollectionEntriesQueryParams = {},
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<PaginatedResponse<CustomCollectionEntryWithFields>>({
    queryKey: queryKeys.customCollections.entries(
      collectionId!,
      params as Record<string, unknown>,
    ),
    queryFn: async () => {
      if (!collectionId) {
        throw new Error('Collection ID is required')
      }
      return customCollectionsApi.getEntries(collectionId, params)
    },
    enabled:
      (options?.enabled !== false &&
        !!collectionId &&
        !!currentOrganization?.id) ??
      false,
    placeholderData: previous => previous,
  })
}

/**
 * Hook to search entries for a custom collection
 */
export function useCustomCollectionEntriesSearchQuery(
  collectionId: string | undefined,
  query: string,
  options?: { enabled?: boolean; limit?: number },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<CustomCollectionEntry[]>({
    queryKey: queryKeys.customCollections.entries(collectionId!),
    queryFn: async () => {
      if (!collectionId) {
        throw new Error('Collection ID is required')
      }
      return customCollectionsApi.searchEntries(
        collectionId,
        query,
        options?.limit,
      )
    },
    enabled:
      (options?.enabled !== false &&
        !!collectionId &&
        !!currentOrganization?.id &&
        !!query) ??
      false,
  })
}

/**
 * Hook to fetch dropdown options for a custom collection
 */
export function useCustomCollectionDropdownOptionsQuery(
  collectionId: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<DropdownOption[]>({
    queryKey: queryKeys.customCollections.dropdownOptions(collectionId!),
    queryFn: async () => {
      if (!collectionId) {
        throw new Error('Collection ID is required')
      }
      return customCollectionsApi.getDropdownOptions(collectionId)
    },
    enabled:
      (options?.enabled !== false &&
        !!collectionId &&
        !!currentOrganization?.id) ??
      false,
  })
}

export function useCustomCollectionFeaturesQuery(
  collectionId: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<CustomCollectionFeatureConfig>({
    queryKey: queryKeys.customCollections.features(collectionId!),
    queryFn: async () => {
      if (!collectionId) {
        throw new Error('Collection ID is required')
      }
      return customCollectionsApi.getCollectionFeatures(collectionId)
    },
    enabled:
      (options?.enabled !== false &&
        !!collectionId &&
        !!currentOrganization?.id) ??
      false,
  })
}

export function useCustomCollectionRelationshipsQuery(
  collectionId: string | undefined,
  direction: 'source' | 'target' | 'both' = 'source',
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<CustomCollectionRelationDefinition[]>({
    queryKey: queryKeys.customCollections.relationships(
      collectionId!,
      direction,
    ),
    queryFn: async () => {
      if (!collectionId) {
        throw new Error('Collection ID is required')
      }
      return customCollectionsApi.getRelationships(collectionId, direction)
    },
    enabled:
      (options?.enabled !== false &&
        !!collectionId &&
        !!currentOrganization?.id) ??
      false,
  })
}

export function useCustomCollectionInverseRelationshipEntriesQuery(
  collectionId: string | undefined,
  entryId: string | undefined,
  relationshipId: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<{
    relationship: CustomCollectionRelationDefinition
    entries: Array<{ id: string; name: string }>
  }>({
    queryKey: queryKeys.customCollections.inverseRelationshipEntries(
      collectionId!,
      entryId!,
      relationshipId!,
    ),
    queryFn: async () => {
      if (!collectionId || !entryId || !relationshipId) {
        throw new Error(
          'Collection ID, entry ID, and relationship ID are required',
        )
      }
      return customCollectionsApi.getInverseRelationshipEntries(
        collectionId,
        entryId,
        relationshipId,
      )
    },
    enabled:
      (options?.enabled !== false &&
        !!collectionId &&
        !!entryId &&
        !!relationshipId &&
        !!currentOrganization?.id) ??
      false,
  })
}

export function useCustomCollectionEntryRelationshipValuesQuery(
  collectionId: string | undefined,
  entryId: string | undefined,
  relationshipId: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<
    Array<{ type: 'CUSTOM_COLLECTION'; id: string; name: string }>
  >({
    queryKey: queryKeys.customCollections.relationshipValues(
      collectionId!,
      entryId!,
      relationshipId!,
    ),
    queryFn: async () => {
      if (!collectionId || !entryId || !relationshipId) {
        throw new Error(
          'Collection ID, entry ID, and relationship ID are required',
        )
      }
      return customCollectionsApi.getEntryRelationshipValues(
        collectionId,
        entryId,
        relationshipId,
      )
    },
    enabled:
      (options?.enabled !== false &&
        !!collectionId &&
        !!entryId &&
        !!relationshipId &&
        !!currentOrganization?.id) ??
      false,
  })
}

// Removed: useCustomCollectionEntryModuleItemsQuery — this backed the old
// chip-list UI (CollectionEntryRelatedModuleItemsTab). All collection entry
// tabs now use the full-featured module tables instead (see
// entry-detail-tabs/{projects,contacts,communications}.tsx).

/**
 * useCustomCollectionsPaginatedQuery under a non-hook name for useModuleTable configs: passing a
 * use*-named import as a value makes React Compiler bail out of the consuming
 * component. Same function, only ever called unconditionally inside the hook.
 */
export const customCollectionsListQuery = useCustomCollectionsPaginatedQuery
